import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Book, Shield, MapPin, Sparkles, User, Sword } from 'lucide-react';

interface SearchResultItem {
  title: string;
  type: string;
  url: string;
  subtitle?: string;
}

export const SearchModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
        else {
          // Open
          const evt = new CustomEvent('open-search-modal');
          window.dispatchEvent(evt);
        }
      }
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [itemsRes, enemiesRes, abilitiesRes, questsRes, npcsRes] = await Promise.all([
          fetch(`/api/items?search=${encodeURIComponent(query)}&limit=4`).then((r) => r.json() as Promise<any>),
          fetch(`/api/collections/enemies?search=${encodeURIComponent(query)}&limit=4`).then((r) => r.json() as Promise<any>),
          fetch(`/api/collections/abilities?search=${encodeURIComponent(query)}&limit=4`).then((r) => r.json() as Promise<any>),
          fetch(`/api/collections/quests?search=${encodeURIComponent(query)}&limit=4`).then((r) => r.json() as Promise<any>),
          fetch(`/api/collections/npcs?search=${encodeURIComponent(query)}&limit=4`).then((r) => r.json() as Promise<any>),
        ]);

        const combined: SearchResultItem[] = [];

        (itemsRes?.items || []).forEach((item: any) => {
          combined.push({
            title: item.name,
            type: 'Item',
            url: `/items#${encodeURIComponent(item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}`,
            subtitle: item.description?.slice(0, 60),
          });
        });

        (enemiesRes?.items || []).forEach((item: any) => {
          combined.push({
            title: item.name,
            type: 'Enemy',
            url: `/enemies#${encodeURIComponent(item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}`,
            subtitle: item.description?.slice(0, 60),
          });
        });

        (abilitiesRes?.items || []).forEach((item: any) => {
          combined.push({
            title: item.name,
            type: 'Ability',
            url: `/abilities#${encodeURIComponent(item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}`,
            subtitle: item.description?.slice(0, 60),
          });
        });

        (questsRes?.items || []).forEach((item: any) => {
          combined.push({
            title: item.name,
            type: 'Quest',
            url: `/quests#${encodeURIComponent(item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}`,
            subtitle: item.description?.slice(0, 60),
          });
        });

        (npcsRes?.items || []).forEach((item: any) => {
          combined.push({
            title: item.name,
            type: 'NPC',
            url: `/npcs#${encodeURIComponent(item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}`,
            subtitle: item.description?.slice(0, 60),
          });
        });

        setResults(combined);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  if (!isOpen) return null;

  const navigateTo = (url: string) => {
    onClose();
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'Item': return <Shield size={16} className="text-primary" />;
      case 'Enemy': return <Sword size={16} className="text-danger" />;
      case 'Ability': return <Sparkles size={16} className="text-warning" />;
      case 'Quest': return <MapPin size={16} className="text-success" />;
      case 'NPC': return <User size={16} className="text-info" />;
      default: return <Book size={16} className="text-secondary" />;
    }
  };

  return (
    <div className="modal-backdrop-custom" onClick={onClose}>
      <div className="search-modal-card card shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="card-header bg-transparent border-bottom p-3 d-flex align-items-center gap-2">
          <Search size={20} className="text-secondary" />
          <input
            ref={inputRef}
            type="search"
            className="form-control form-control-lg border-0 shadow-none bg-transparent"
            placeholder="Search across all 34 tables (items, enemies, quests, npcs, abilities...)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn-sm btn-link text-secondary p-0" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="card-body p-0 max-h-400 overflow-y-auto">
          {loading && (
            <div className="p-4 text-center text-secondary">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
              Searching compendium...
            </div>
          )}

          {!loading && query.trim() && results.length === 0 && (
            <div className="p-4 text-center text-secondary">
              No results found for &ldquo;{query}&rdquo;.
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="list-group list-group-flush">
              {results.map((res, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-3"
                  onClick={() => navigateTo(res.url)}
                >
                  <div className="d-flex align-items-center gap-3">
                    <span className="p-2 rounded bg-body-tertiary">{getIcon(res.type)}</span>
                    <div>
                      <div className="fw-semibold">{res.title}</div>
                      {res.subtitle && <small className="text-secondary">{res.subtitle}</small>}
                    </div>
                  </div>
                  <span className="badge bg-secondary-subtle text-secondary">{res.type}</span>
                </button>
              ))}
            </div>
          )}

          {!loading && !query.trim() && (
            <div className="p-4 text-center text-secondary small">
              Type to search items, abilities, enemies, quests, NPCs, skills, classes, or locations.
            </div>
          )}
        </div>

        <div className="card-footer bg-body-tertiary border-top p-2 px-3 d-flex justify-content-between align-items-center small text-secondary">
          <span>Navigate with click</span>
          <span><kbd>ESC</kbd> to close &bull; <kbd>Ctrl+K</kbd> to toggle</span>
        </div>
      </div>
    </div>
  );
};
